import dayjs from 'dayjs'
import React from 'react'

import { CountryFlag, DateString, NumberString, PriceString } from '@fourthwall/components'

import { AnalyticsPaymentMethodLabel } from '../../../../analyticsModule/components/AnalyticsPaymentMethod/AnalyticsPaymentMethodLabel'
import type { AnalyticsPaymentMethodLabelType } from '../../../../analyticsModule/components/AnalyticsPaymentMethod/AnalyticsPaymentMethodLabel.types'
import { AnalyticsSocialPlatformLabel } from '../../../../analyticsModule/components/AnalyticsSocialPlatform/AnalyticsSocialPlatformLabel'
import type { AnalyticsSocialPlatformLabelType } from '../../../../analyticsModule/components/AnalyticsSocialPlatform/AnalyticsSocialPlatformLabel.types'
import type {
  AnalyticsTableCellDecorator,
  AnalyticsTableCellType,
} from '../../../../analyticsModule/components/AnalyticsTable/AnalyticsTable.types'
import type { AnalyticsPrecision } from '../../../../analyticsModule/types'
import { normalizeLabel } from '../../../../utils/normalizeLabel'

const analyzeDateRange = (dateRange: [Date, Date]) => {
  const startDate = dayjs(dateRange[0])
  const endDate = dayjs(dateRange[1])

  return {
    isSameDay: startDate.isSame(endDate, 'day'),
    isSameMonth: startDate.isSame(endDate, 'month'),
    isSameYear: startDate.isSame(endDate, 'year'),
  }
}

const getDateFormat = (
  precision: AnalyticsPrecision,
  rangeAnalysis: ReturnType<typeof analyzeDateRange>,
): string => {
  const { isSameDay, isSameMonth, isSameYear } = rangeAnalysis

  switch (precision) {
    case 'hour':
      return isSameDay ? 'h:mm A' : 'MMM D h:mm A'

    case 'day':
      if (isSameMonth) return 'MMM D'
      if (isSameYear) return 'MMM D'

      return 'MMM D, YYYY'

    case 'week':
      return isSameYear ? '[Week] w' : '[Week] w, YYYY'

    case 'month':
      return isSameYear ? 'MMMM' : 'MMM, YYYY'

    case 'quarter':
      return isSameYear ? '[Q]Q' : '[Q]Q, YYYY'

    case 'year':
      return 'YYYY'

    default:
      return 'MMM D, YYYY'
  }
}

export const formatChartDate = (
  value: string,
  dateRange: [Date, Date],
  precision: AnalyticsPrecision,
) => {
  const rangeAnalysis = analyzeDateRange(dateRange)
  const format = getDateFormat(precision, rangeAnalysis)

  return <DateString date={value} format={format} />
}

export const formatChartLabel = ({
  value,
  type,
  decorator,
  dateRange,
  precision,
}: {
  value: string
  // TODO: change type
  type: AnalyticsTableCellType
  dateRange: [Date, Date]
  precision: AnalyticsPrecision
  // TODO: change type
  decorator?: AnalyticsTableCellDecorator
}) => {
  if (type === 'date') {
    return formatChartDate(value, dateRange, precision)
  }

  if (type === 'money') {
    return <PriceString value={value} />
  }

  if (type === 'number') {
    return <NumberString value={Number(value)} />
  }

  if (type === 'string' && decorator === 'country') {
    if (value === 'null') return 'Unidentified'

    return <CountryFlag countryCode={value} />
  }

  if (type === 'string' && decorator === 'payment_method') {
    return <AnalyticsPaymentMethodLabel type={value as AnalyticsPaymentMethodLabelType} />
  }

  if (type === 'string' && decorator === 'social_platform') {
    return <AnalyticsSocialPlatformLabel type={value as AnalyticsSocialPlatformLabelType} />
  }

  return normalizeLabel(value)
}
